import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import type { CardVariant } from '@s4l/ds'
import { Card, Rating, StarRating } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import { Link } from '@s4l/react-facade'
import { Price as ModelPrice } from '@s4l/stayforlong/kernel/Price'
import { Property } from '@s4l/stayforlong/property/domain/Property'
import { Language } from '@s4l/stayforlong/shared/domain/Language'
import { Market } from '@s4l/stayforlong/shared/domain/Market'
import type { Matches } from '@s4l/utils/hooks/useContainerQueryFallback'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import type { PropsWithChildren, ReactNode } from 'react'
import React, { Children, isValidElement, useMemo, useState } from 'react'

import Image from '../../Atoms/Image'
import { ActionLikeUnlike } from './ActionLikeUnlike'
import { ActionRemove } from './ActionRemove'
import { ActionClose } from './ActionClose'
import { Actions } from './Actions'
import { PropertyCardContext } from './context'
import { Facilities } from '../../Atoms/Facilities'
import { FooterCTA } from './FooterCTA'
import cn from 'classnames'
import { FooterPrice } from './FooterPrice'
import styles from './styles.module.css'
import PromotionTag from '../../Atoms/PromotionTag'
import { PartialSearch } from '@s4l/stayforlong/shared/domain/PartialSearch'
import debug from 'debug'

const log = debug('s4l:uikit:PropertyCard')

const MAX_FACILITIES_TO_SHOW = 3

interface AffiliateParams {
  aid?: string
  source?: string
  total?: string
  subtotal?: string
  taxes?: string
  offer_type?: string
}

interface IPropertyCardProps {
  propertyJSON: ReturnType<Property['toJSON']>
  variant?: CardVariant
  onClick?: () => void
  affiliateParams?: AffiliateParams
  checkInOffset?: number
}

export function PropertyCard({
  children,
  propertyJSON,
  variant = 'default',
  onClick,
  affiliateParams,
  checkInOffset = 0,
  ...rest
}: PropsWithChildren<IPropertyCardProps>) {
  const { i18n } = useS4LContext()
  const {
    data: market = Market.default(),
    loading: isLoadingMarket,
    error: marketError,
  } = useUseCase('GetCurrentMarketSharedUseCase')

  if (marketError) {
    log('Error in GetCurrentMarketSharedUseCase')
  }
  const {
    data: language = Language.default(),
    loading: isLoadingLanguage,
    error: languageError,
  } = useUseCase('GetCurrentLanguageSharedUseCase')
  if (languageError) {
    log('Error in GetCurrentLanguageSharedUseCase')
  }

  const {
    data: affiliate,
    loading: isLoadingAffiliate,
    error: affiliateError,
  } = useUseCase('GetCurrentAffiliateSharedUseCase')
  if (affiliateError) {
    log('Error in GetCurrentAffiliateSharedUseCase')
  }

  const {
    data: source,
    loading: isLoadingSource,
    error: sourceError,
  } = useUseCase('GetCurrentSourceSharedUseCase')
  if (sourceError) {
    log('Error in GetCurrentSourceSharedUseCase')
  }

  const partialSearch = PartialSearch.fromCheckInOffset(checkInOffset)

  const [matches, setMatches] = useState<Matches>({
    small: false,
    medium: false,
    large: false,
  })

  const contextValue = useMemo(() => {
    const property = propertyJSON
      ? Property.fromJSONOrNull({ json: propertyJSON }) || undefined
      : undefined
    const price = property?.price ? ModelPrice.fromJSON(property.price) : undefined
    const isSmallCardVariant = !matches.medium && !matches.large && variant === 'default'
    const isSoldOut = property?.isSoldOut() || false

    return {
      variant,
      price,
      property,
      isSmallCardVariant,
      isSoldOut,
    }
  }, [variant, propertyJSON, matches])

  const { isSmallCardVariant, isSoldOut, property } = contextValue
  const isSmallOrLiteCardVariant = isSmallCardVariant || variant === 'lite'
  const isLiteCardVariant = variant === 'lite'

  if (!propertyJSON) return null

  const footerChildren: ReactNode[] = []
  const actionChildren: ReactNode[] = []
  let closeButton: ReactNode = null

  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return
    }

    if (child.type === PropertyCard.FooterPrice || child.type === PropertyCard.FooterCTA) {
      footerChildren.push(child)
    } else if (child.type === PropertyCard.ActionClose) {
      closeButton = child
    } else if (
      child.type === PropertyCard.ActionRemove ||
      child.type === PropertyCard.ActionLikeUnlike ||
      child.type === PropertyCard.Actions
    ) {
      actionChildren.push(child)
    }
  })

  if (!property) return null

  const onContainerQueryChange = (newMatches: Matches) => {
    setMatches(newMatches)
  }
  const distance = property.distanceByMarket({ market })

  const fromCenter =
    distance !== null
      ? i18n.formatMessage({ id: 'accommodation.center-distance' }, { distance })
      : undefined

  const featuredText = property.isFoundOn({ affiliate, source })
    ? `👇 ${i18n.formatMessage({ id: 'global.found-on' })} ${affiliate?.capitalizedValue}`
    : undefined

  const isLoading = isLoadingMarket || isLoadingLanguage || isLoadingAffiliate || isLoadingSource
  return (
    <Card
      data-testid={isLoading ? 'property-card-loading' : 'property-card'}
      variant={variant}
      url={property.url(
        market,
        language,
        affiliateParams,
        checkInOffset > 0 ? partialSearch : undefined
      )}
      asLink={Link}
      onClick={onClick}
      {...rest}
      featured={property.isHighlighted()}
      featuredText={featuredText}
      onContainerQueryChange={onContainerQueryChange}
    >
      <PropertyCardContext.Provider value={contextValue}>
        <Card.Header>
          {actionChildren.length > 0 && <Card.Action>{actionChildren}</Card.Action>}
          <Card.Images>
            {property.imagesMap(image => (
              <Image.Model
                key={image.toBase64()}
                image={image}
                alt={property.name}
                preset="propertyCard"
              />
            ))}
          </Card.Images>
        </Card.Header>
        <Card.Body variant={variant}>
          <Card.Main>
            <div className={styles.mainHeader}>
              <div className={styles.titleSection}>
                <Card.Title>{property.name}</Card.Title>
                <StarRating rating={property.stars} />
                <Card.Address
                  center={property.inCenter}
                  i18n={{
                    inCenter: i18n.formatMessage({ id: 'accommodation.in-center' }),
                    fromCenter,
                  }}
                  hide={isLiteCardVariant}
                >
                  {property.city}
                </Card.Address>
              </div>
              {closeButton}
            </div>
          </Card.Main>
          <Card.Details>
            {property.hasRating() && (
              <Rating
                value={property.rating}
                size={isLiteCardVariant ? 'lite' : 'small'}
                comments={property.numberOfComments}
                i18n={{
                  quality: {
                    poor: i18n.formatMessage({ id: 'score.range-5' }),
                    fair: i18n.formatMessage({ id: 'score.range-4' }),
                    good: i18n.formatMessage({ id: 'score.range-3' }),
                    veryGood: i18n.formatMessage({ id: 'score.range-2' }),
                    excellent: i18n.formatMessage({ id: 'score.range-1' }),
                  },
                  basedOn: i18n.formatMessage({ id: 'global.score-based' }),
                  reviews: i18n.formatMessage({ id: 'global.reviews' }),
                }}
              />
            )}

            {isSoldOut && (
              <Card.Info>{i18n.formatMessage({ id: 'global.dates-not-available' })}</Card.Info>
            )}

            <div
              className={cn(styles.facilitiesContainer, {
                [styles.facilitiesContainerFixedHeight]: variant === 'fixed-height',
              })}
            >
              <Facilities
                facilitiesJSON={propertyJSON.facilities}
                hide={isSmallOrLiteCardVariant}
                maxFacilities={MAX_FACILITIES_TO_SHOW}
                size="s"
              />
            </div>
            {property.percentageDiscount() && (
              <Card.Tag>
                <PromotionTag
                  discount={property.percentageDiscount() as number}
                  prime={property.isPrime}
                  variant={isSmallCardVariant ? 'plain' : 'rounded'}
                />
              </Card.Tag>
            )}
          </Card.Details>
          {footerChildren.length > 0 && (
            <Card.Footer>
              {isSmallOrLiteCardVariant ? (
                <div className={styles.mobileFooter}>
                  <div className={styles.priceContainer}>
                    {footerChildren.find(child => {
                      return isValidElement(child) && child.type === PropertyCard.FooterPrice
                    })}
                  </div>
                  {!isLiteCardVariant && (
                    <div className={styles.ctaContainer}>
                      {footerChildren.find(child => {
                        return isValidElement(child) && child.type === PropertyCard.FooterCTA
                      })}
                    </div>
                  )}
                </div>
              ) : (
                footerChildren
              )}
            </Card.Footer>
          )}
        </Card.Body>
      </PropertyCardContext.Provider>
    </Card>
  )
}

PropertyCard.ActionRemove = withErrorBoundary(ActionRemove)
PropertyCard.ActionLikeUnlike = withErrorBoundary(ActionLikeUnlike)
PropertyCard.ActionClose = withErrorBoundary(ActionClose)
PropertyCard.Actions = withErrorBoundary(Actions)
PropertyCard.FooterPrice = withErrorBoundary(FooterPrice)
PropertyCard.FooterCTA = withErrorBoundary(FooterCTA)
PropertyCard.Placeholder = withErrorBoundary(Card.Placeholder)

PropertyCard.displayName = 'PropertyCard'
export default withErrorBoundary(PropertyCard)
